import type { User } from "firebase/auth";

export type GmAccessRole = "none" | "gm" | "owner";

export type GmAccess = {
  role: GmAccessRole;
  /** สลับโหมด GM ในเกม (ให้ EXP / สเตตมอนสเตอร์) */
  canUseInGameGmMode: boolean;
  /** หน้า GM Console (/gm) — owner เท่านั้น */
  canAccessGmConsole: boolean;
  /** มี claim gm/owner จาก Firebase (หลัง syncGmClaims) */
  verifiedByClaims: boolean;
};

function envFlag(name: keyof ImportMetaEnv): boolean {
  const value = import.meta.env[name];
  return value === "true" || value === "1";
}

function parseList(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(/[,;\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

function normalizeEmail(user: User): string | null {
  const email = user.email?.trim().toLowerCase();
  return email && email.length > 0 ? email : null;
}

function roleRank(role: GmAccessRole): number {
  switch (role) {
    case "owner":
      return 2;
    case "gm":
      return 1;
    default:
      return 0;
  }
}

function maxRole(a: GmAccessRole, b: GmAccessRole): GmAccessRole {
  return roleRank(a) >= roleRank(b) ? a : b;
}

function roleFromEnv(user: User): GmAccessRole {
  const email = normalizeEmail(user);
  const uid = user.uid.trim().toLowerCase();
  const ownerEmail = parseList(import.meta.env.VITE_GM_OWNER_EMAIL)[0];
  if (ownerEmail && email === ownerEmail) return "owner";

  if (envFlag("VITE_GM_ALLOW_ALL")) return "gm";

  const emails = parseList(import.meta.env.VITE_GM_EMAILS);
  const uids = parseList(import.meta.env.VITE_GM_UIDS);
  if (email && emails.includes(email)) return "gm";
  if (uids.includes(uid)) return "gm";
  return "none";
}

function roleFromClaims(claims: Record<string, unknown> | null | undefined): GmAccessRole {
  if (!claims) return "none";
  if (claims.owner === true) return "owner";
  if (claims.gm === true) return "gm";
  return "none";
}

/** รวม allowlist ฝั่ง client กับ custom claims (ใช้ role ที่สูงกว่า). */
export function resolveGmAccess(
  user: User | null,
  claims?: Record<string, unknown> | null,
): GmAccess {
  if (!user) {
    return {
      role: "none",
      canUseInGameGmMode: false,
      canAccessGmConsole: false,
      verifiedByClaims: false,
    };
  }

  const envRole = roleFromEnv(user);
  const claimRole = roleFromClaims(claims);
  const role = maxRole(envRole, claimRole);
  const verifiedByClaims = claimRole !== "none";

  const canUseInGameGmMode = role === "gm" || role === "owner";
  const canAccessGmConsole = role === "owner";

  return {
    role,
    canUseInGameGmMode,
    canAccessGmConsole,
    verifiedByClaims,
  };
}
